import React, { useState, useEffect, useCallback } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import './App.css';

const API_BASE_URL = process.env.NODE_ENV === "development"
  ? "http://localhost:5000"  // Local Flask server
  : "https://hypothesaes-app-b508a899af69.herokuapp.com";  // Deployed app


function App() {
  const [results, setResults] = useState([]);
  const [details, setDetails] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortedResults, setSortedResults] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [currentDataset, setCurrentDataset] = useState('headlines');
  const [isLoading, setIsLoading] = useState(false);

  const handleInitial = async (dataset) => {
    const response = await fetch(`${API_BASE_URL}/initial/${dataset}`);
    const data = await response.json();
    setResults(data);
  };

  const fetchDetails = async (dataset, id) => {
    const response = await fetch(`${API_BASE_URL}/details/${dataset}/${id}`);
    const data = await response.json();
    setDetails(data);
  };

  const handleDatasetChange = async (dataset) => {
    if (dataset === currentDataset) return;
    setCurrentDataset(dataset);
    setIsLoading(true);
    handleInitial(dataset);
    setIsLoading(false);
  };

  // Custom Tooltip to show item details on hover
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <div style={{ background: 'white', padding: 10, border: '1px solid black'}}>
          <p>Neuron {item.id}: {item.interpretation}</p>
        </div>
      );
    }
    return null;
  };

  const toggleRow = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  useEffect(() => {
    handleInitial('headlines');
  }, []);

  useEffect(() => {
    if (results.length > 0) {
      fetchDetails(currentDataset, results[0].id);
    }
  }, [results]);

  useEffect(() => {
    const sorted = sortOrder === 'desc' ? [...results] : [...results].reverse();
    setSortedResults(sorted);
  }, [results, sortOrder]);

  const getTaskDescription = (dataset) => {
    const descriptions = {
      'yelp': 'Hypothesize concepts that predict Yelp rating (1-5 stars).',
      'congress': 'Hypothesize concepts that predict Congressional speech party affiliation (Republican 1, Democrat 0).',
      'headlines': 'Hypothesize concepts that predict news headline popularity (# clicks).'
    };
    return descriptions[dataset];
  };

  const getDataInputDescription = (dataset) => {
    const descriptions = {
      'yelp': 'Restaurant reviews',
      'congress': 'Congressional speech excerpts',
      'headlines': 'News headlines'
    };
    return descriptions[dataset];
  };

  return (
    <div>
      <div className="responsive-container">
        <div className="paper">
          <strong>Paper:</strong>{" "}
          <a href="https://arxiv.org/abs/2502.04382">
            Sparse Autoencoders for Hypothesis Generation
          </a>
          . Rajiv Movva*, Kenny Peng*, Nikhil Garg, Jon Kleinberg, Emma Pierson.
        </div>
        <div className="title">
          <h1>HypotheSAEs</h1>
          {/* <h3>Generate hypotheses from text data</h3> */}
        </div>
        <div className="code">
          <strong>Use our code!</strong>
          <br />
          <code>pip install hypothesaes</code>
          <br />
          <a href="https://github.com/rmovva/HypotheSAEs">GitHub Repo</a>
        </div>
      </div>

      <div className="dataset-tabs">
      <button 
          className={`tab-button ${currentDataset === 'headlines' ? 'active' : ''}`}
          onClick={() => handleDatasetChange('headlines')}
          disabled={isLoading}
        >
          Headlines
        </button>
        <button 
          className={`tab-button ${currentDataset === 'congress' ? 'active' : ''}`}
          onClick={() => handleDatasetChange('congress')}
          disabled={isLoading}
        >
          Congress
        </button>
        <button 
          className={`tab-button ${currentDataset === 'yelp' ? 'active' : ''}`}
          onClick={() => handleDatasetChange('yelp')}
          disabled={isLoading}
        >
          Yelp
        </button>
      </div>

      <h3 style={{textAlign: 'center', fontWeight: 'normal'}}>
        <strong>Task:</strong> {getTaskDescription(currentDataset)}
      </h3>
      <table className="scrollable-table">
        <thead>
          <tr>
            <th class="no-mobile">SAE Neuron ID<br/><span style={{fontWeight: 'normal'}}>SAE neurons tend to be interpretable.</span></th>
            <th 
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              className="sortable-header"
            >
              Neuron Correlation {sortOrder === 'desc' ? '▲' : '▼'} <br/><span style={{fontWeight: 'normal'}}>Correlation of neuron with target variable.</span>
            </th>
            <th>LLM-Generated Interpretation<br/><span style={{fontWeight: 'normal'}}>Based on texts that activate the neuron.</span></th>
            <th class="no-mobile">Interpretation Fidelity<br/><span style={{fontWeight: 'normal'}}>How well the interpretation matches the neuron.</span></th>
          </tr>
        </thead>
        <tbody style={{height: '250px', overflowY: 'auto' }}>
          {sortedResults.map((item) => (
            <tr 
              key={item.id}
              onClick={() => fetchDetails(currentDataset, item.id)}
              className={details && details.id === item.id ? 'selected-row' : ''}
            >
              <td class="no-mobile">{item.id}</td>
              <td>{item.neuron_corr.toFixed(3)}</td>
              <td>{item.interpretation}</td>
              <td class="no-mobile">{item.interp_fidelity_F1.toFixed(3)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div class='umap-neurons-container'>
        <div class='umap'>
          <h2>Neuron UMAP</h2>
          <p>Click to see neuron details!</p>
          <p>
            {currentDataset === 'congress' ? (
              <><span style={{color: '#ff0000'}}>●</span> Republican, <span style={{color: '#0000ff'}}>●</span> Democrat</>
            ) : currentDataset === 'headlines' ? (
              <><span style={{color: '#00cc00'}}>●</span> more clicks, <span style={{color: '#cc0000'}}>●</span> fewer clicks</>
            ) : (
              <><span style={{color: '#00cc00'}}>●</span> higher rating, <span style={{color: '#cc0000'}}>●</span> lower rating</>
            )}
          </p>
          <ResponsiveContainer width="100%" height={500}>
            <ScatterChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
              <XAxis 
                type="number" 
                dataKey="activation_umap_x" 
                hide={true}
                domain={['dataMin', 'dataMax']} 
              />
              <YAxis 
                type="number" 
                dataKey="activation_umap_y" 
                hide={true}
                domain={['dataMin', 'dataMax']}
              />
              <Tooltip content={<CustomTooltip />} />
              <Scatter 
                data={results
                  .map((item) => {
                    let red, green, blue;

                    if (currentDataset === 'congress') {
                      if (item.neuron_corr <= 0) {
                          // Transition from grey (200, 200, 200) to blue (0, 0, 255)
                          const t = (item.neuron_corr + 0.12) / 0.12; // Scale [-0.15, 0] to [0,1]
                          blue = Math.round(255 * (1 - t) + 200 * t);
                          green = Math.round(200 * t);
                          red = Math.round(200 * t);
                      } else {
                          //Transition from red (255, 0, 0) to grey (200, 200, 200)
                          const t = item.neuron_corr / 0.12; // Scale [0, 0.15] to [0,1]
                          blue = Math.round(200 * (1 - t));
                          green = Math.round(200 * (1 - t));
                          red = Math.round(200 * (1 - t) + 255 * t);
                      }
                      const color = `rgba(${red}, ${green}, ${blue}, 0.75)`
                      return {
                        ...item,
                        fill: color,
                        stroke: details && details.id === item.id ? '#000' : 'none',
                        strokeWidth: details && details.id === item.id ? 2 : 0
                      };
                    } else if (currentDataset === 'headlines') {
                      if (item.neuron_corr <= 0) {
                        // Transition from grey (200, 200, 200) to red (255, 0, 0)
                        const t = (item.neuron_corr + 0.15) / 0.15; // Scale [-0.15, 0] to [0,1]
                        red = Math.round(255 * (1 - t) + 200 * t);
                        green = Math.round(200 * t);
                        blue = Math.round(200 * t);
                      } else {
                          //Transition from green (0, 255, 0) to grey (200, 200, 200)
                          const t = item.neuron_corr / 0.15; // Scale [0, 0.15] to [0,1]
                          blue = Math.round(200 * (1 - t));
                          red = Math.round(200 * (1 - t));
                          green = Math.round(200 * (1 - t) + 255 * t);
                      }
                      const color = `rgba(${red}, ${green}, ${blue}, 0.75)`
                      return {
                        ...item,
                        fill: color,
                        stroke: details && details.id === item.id ? '#000' : 'none',
                        strokeWidth: details && details.id === item.id ? 2 : 0
                      };
                    } else {
                        if (item.neuron_corr <= 0) {
                          // Transition from grey (200, 200, 200) to red (255, 0, 0)
                          const t = (item.neuron_corr + 0.15) / 0.15; // Scale [-0.15, 0] to [0,1]
                          red = Math.round(255 * (1 - t) + 200 * t);
                          green = Math.round(200 * t);
                          blue = Math.round(200 * t);
                      } else {
                          //Transition from green (0, 255, 0) to grey (200, 200, 200)
                          const t = item.neuron_corr / 0.15; // Scale [0, 0.15] to [0,1]
                          blue = Math.round(200 * (1 - t));
                          red = Math.round(200 * (1 - t));
                          green = Math.round(200 * (1 - t) + 255 * t);
                      }
                      const color = `rgba(${red}, ${green}, ${blue}, 0.75)`
                      return {
                        ...item,
                        fill: color,
                        stroke: details && details.id === item.id ? '#000' : 'none',
                        strokeWidth: details && details.id === item.id ? 2 : 0
                      };
                    }
                  })}
                onClick={(e) => fetchDetails(currentDataset, e.id)}
                isAnimationActive={false}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        {details && (
          <div class='neuron-details'>
            <h2>Neuron {details.id} | {details.interpretation}</h2>
            <p>Neuron Correlation: {details.neuron_corr.toFixed(3)}</p>
            <p>Interpretation Fidelity: {details.interp_fidelity_F1.toFixed(3)}</p>
            <table className="scrollable-table">
              <thead>
                <tr>
                  <th>{getDataInputDescription(currentDataset)} that most activate the neuron</th>
                </tr>
              </thead>
              <tbody>
                {details.top_texts && details.top_texts.map((text, index) => (
                  <tr key={index}>
                    <td>
                      {text.length > 280 ? (
                        <div>
                          {expandedRows.has(index) ? (
                            <div>
                              {text}
                              <span 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRow(index);
                                }}
                                style={{ cursor: 'pointer', marginLeft: '5px' }}
                              >
                                ▼
                              </span>
                            </div>
                          ) : (
                            <div>
                              {text.slice(0, 280)}...
                              <span 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRow(index);
                                }}
                                style={{ cursor: 'pointer', marginLeft: '5px' }}
                              >
                                ▶
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>{text}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;